import { createThunk, model, Model } from 'mst-collection';
import { types } from 'mobx-state-tree';
import { nanoid } from 'nanoid/non-secure';

import { Api, IPostPhotoResponse, IPostVideoResponse } from '@api';
import { formData } from '@utils';
import { ThunkAbortController } from '../utils';
import { MediaTypeEnum } from '@constants';
import { XOR } from '@types';

export class UploadMedia extends Model({
  isUploaded: false,
  id: types.optional(types.string, () => nanoid()),
  uri: types.string,
  type: types.enumeration(Object.values(MediaTypeEnum)),
  coverUri: types.maybe(types.string),
}) {
  afterAttach() {
    // For case when we want to edit some entity (post for example) we can add here already uploaded
    // media
    if (this.isUploaded) {
      return;
    }

    this.upload.run();
  }

  get isUploadingOrError() {
    return this.upload.inProgress || this.upload.isError;
  }

  get previewUri() {
    return this.type === MediaTypeEnum.Video
      ? this.coverUri
      : this.uri;
  }

  private _setUploadData(
    data: XOR<IPostPhotoResponse, IPostVideoResponse>,
  ) {
    this.id = data.id;
    this.coverUri = data.videoCover;
    this.isUploaded = true;
  }

  abortUpload() {
    // TODO: may should me make remove request?
    if (this.upload.inProgress) {
      this._uploadAbortController.abort();
    }
  }

  private _uploadAbortController = new ThunkAbortController();

  private async _uploadPhoto() {
    const data = await formData(this.uri, 'imageFile');
    const res = await Api.Feed.uploadPhoto(
      data,
      this._uploadAbortController.create(),
    );

    return res;
  }

  private async _uploadVideo() {
    const data = await formData(this.uri, 'file');
    const res = await Api.Feed.uploadVideo(
      data,
      this._uploadAbortController.create(),
    );

    return res;
  }

  upload = createThunk(() => {
    return async function upload(this: UploadMedia, flow) {
      try {
        flow.start();

        let data: XOR<IPostPhotoResponse, IPostVideoResponse>;

        if (this.type === MediaTypeEnum.Video) {
          const res = await this._uploadVideo();
          data = res.data;
        } else {
          const res = await this._uploadPhoto();
          data = res.data;
        }

        this._uploadAbortController.remove();
        this._setUploadData(data);

        flow.success();
      } catch (error) {
        if (ThunkAbortController.isCanceled(error)) {
          return;
        }

        flow.failed(error as Error);
      }
    };
  }, false);
}

export const UploadMediaModel = model(UploadMedia);
